import React, { useEffect, useState } from "react";
import Question, { AnswerTypes } from "../../../types/questionVM";
import JobType from "../../../types/jobType";
import { Field, FormikErrors } from "formik";
import { Inputs } from "../../formInputs";
import { useDispatch, useSelector } from "../../../store";
import { searchCategories } from "../../../slices/category";
import SearchParams, { defaultParams } from "../../../types/searchParams";
import { debounce } from "lodash";
import { searchJobSource } from "../../../slices/jobSource";
import { searchCommunicationMethod } from "../../../slices/communicationMethod";
import { searchSite } from "../../../slices/site";
import { Divider, Grid, Typography } from "@mui/material";
import ColumnDef from "../../../types/columnDef";
import moment from "moment";
import { Base } from "../../base";
import JobMilestone from "../../../types/jobMilestone";
import theme from "../../../theme";
import { searchJobByType } from "../../../slices/job";
import KeyValue from "../../../types/keyValue";
import { RemoveCircle } from "@mui/icons-material";
import { closestCenter, DndContext, DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableSitePreview from "./SortableSitePreview";

export interface CustomJobType extends JobType {
  highlight: boolean;
  start: Date;
  end?: Date;
  milestoneTitle: string;
}

interface Props {
  question: Question;
  index: number;
  values: CustomJobType;
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<CustomJobType>>;
}

const QuestionPreview: React.FC<Props> = (props) => {
  const { question, index, values, setFieldValue } = props;
  const dispatch = useDispatch();

  const { categorySearchResults } = useSelector((store) => store.category);
  const { jobSourceSearchResults } = useSelector((store) => store.jobSource);
  const { communicationMethodSearchResults } = useSelector(
    (store) => store.communication
  );
  const { siteSearchResults } = useSelector((store) => store.site);
  const [temporaryMilestones, setTemporaryMilestones] = useState<
    JobMilestone[]
  >([]);
  const [deleteMilestone, setDeleteMilestone] = useState<{
    id?: number;
    value?: string;
  }>();

  const [siteFilter, setSiteFilter] = useState<SearchParams>(defaultParams);
  const [jobFilter, setJobFilter] = useState<SearchParams>({
    ...defaultParams,
    filters: question.filterSelectedJobStatuses.map((j) => {
      return `jobstatus${j.id}`;
    }),
  });
  const [jobByTypeResults, setJobByTypeResults] = useState<KeyValue[]>([]);
  const [tempSites, setTempSites] = useState<
    { id?: number; name?: string; order?: number }[]
  >([]);

  useEffect(() => {
    switch (question.answerType.id) {
      case AnswerTypes.Category:
        if (values.id > 0) {
          const filters: string[] = [];
          filters.push(`jobtype:${values.id}`);
          const searchParams: SearchParams = {
            ...defaultParams,
            filters: filters,
            orderBy: "name",
            orderDirection: "Ascending",
          };
          dispatch(searchCategories(searchParams));
        }
        break;
      case AnswerTypes.Source:
        dispatch(searchJobSource(defaultParams));
        break;
      case AnswerTypes.Method:
        dispatch(searchCommunicationMethod(defaultParams));
        break;
      case AnswerTypes.Site:
        dispatch(searchSite(defaultParams));
        break;
      case AnswerTypes.Job:
        if (!!question.filterSelectedJobType) {
          let filters: string[] = [];
          // TODO: Finish setup and testing before moving to display job question component
          if (!!question.filterSelectedJobStatuses.length) {
            question.filterSelectedJobStatuses.forEach((s) => {
              filters.push(`jobstatus:${s.id}`);
            });
          } else if (!!question.filterSelectedJobType) {
            filters.push(`jobtype:${question.filterSelectedJobType.id}`);
          }
          setJobFilter({ ...jobFilter, filters });
          dispatch(
            searchJobByType({
              ...defaultParams,
              filters,
            })
          ).then((result: any) => {
            setJobByTypeResults(result.payload.results);
          });
        }
        break;

      default:
        break;
    }
  }, [
    dispatch,
    question.answerType.id,
    question.filterSelectedJobStatuses,
    question.filterSelectedJobType,
    values.id,
  ]);

  const updateCategorySearch = debounce((query: string) => {
    dispatch(
      searchCategories({
        ...defaultParams,
        filters: [`jobtype:${values.id}`],
        query: query,
      })
    );
  }, 500);

  const updateJobSourceSearch = debounce((query: string) => {
    dispatch(
      searchJobSource({
        ...defaultParams,
        query: query,
      })
    );
  }, 500);

  const updateCommMethodSearch = debounce((query: string) => {
    dispatch(
      searchCommunicationMethod({
        ...defaultParams,
        query: query,
      })
    );
  }, 500);

  const updateSiteSearch = debounce((query: string) => {
    dispatch(searchSite({ ...siteFilter, query }));
    setSiteFilter({ ...siteFilter, query });
  }, 500);

  const updateJobSearch = debounce((query: string) => {
    dispatch(searchJobByType({ ...jobFilter, query })).then((result: any) => {
      setJobByTypeResults(result.payload.results);
    });
    setJobFilter({ ...jobFilter, query });
  }, 500);

  const handleAddMilestone = (
    values: { start: Date; end?: Date; title?: string },
    setFieldValue: (
      field: string,
      value: any,
      shouldValidate?: boolean
    ) => Promise<void | FormikErrors<CustomJobType>>
  ) => {
    setTemporaryMilestones([
      ...temporaryMilestones,
      {
        id: temporaryMilestones.length + 1,
        start: values.start,
        end: values.end,
        title: values.title,
      },
    ]);
    setFieldValue("milestone", undefined);
    setFieldValue("milestoneTitle", "");
  };

  const columns: ColumnDef[] = [
    {
      field: "milestone",
      headerName: "Date",
      align: "center",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "name",
      headerName: "Title",
      align: "center",
      headerAlign: "center",
      flex: 1,
      headerClassName: "header",
      mobileSize: 12,
    },
    {
      field: "actions",
      headerName: "Actions",
      headerAlign: "center",
      align: "center",
      flex: 1,
      headerClassName: "header",
      sortable: false,
      disableColumnMenu: true,
      disableExport: true,
      filterable: false,
      renderCell: (params) => <>{params.value}</>,
      mobileSize: 12,
    },
  ];

  const displayRows = (array: any[]) => {
    return array.map((milestone) => {
      let rowObject = {
        id: milestone.id,
        name: milestone.title,
        milestone: `${moment(milestone.milestone).format("MM/DD/YYYY")}`,
        actions: (
          <Base.TableActions
            id={milestone.id!}
            handleDelete={() => {
              setDeleteMilestone({ id: milestone.id, value: milestone.title });
            }}
          />
        ),
      };
      return rowObject;
    });
  };

  const reorder = (e: DragEndEvent) => {
    const { over, active } = e;

    if (!over) return;

    const oldIndex = tempSites.indexOf(
      tempSites.find((s) => s.order === active.id)!
    );
    const newIndex = tempSites.indexOf(
      tempSites.find((s) => s.order === over.id)!
    );

    const move = (from: number, to: number) => {
      setTempSites((items) => {
        const updated = [...items];
        const [movedItem] = updated.splice(from, 1);
        updated.splice(to, 0, movedItem);
        return updated;
      });
    };
    move(oldIndex, newIndex);
  };

  switch (question.answerType.id) {
    case AnswerTypes.Text:
    case AnswerTypes.Phone:
    case AnswerTypes.Title:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Text}
          inputProps={{ maxLength: 100 }}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Status:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Dropdown}
          options={values.statuses.map((s) => {
            return { id: s.id, value: s.name };
          })}
          staticFilter
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Checkbox:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Checkbox}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Multiline:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.TextArea}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Dates:
      return (
        <Grid container columnSpacing={2} mt={2}>
          <Grid size={{ sm: 3, xs: 12 }}>
            <Field name="start" label="Date" component={Inputs.Date} />
          </Grid>
          <Grid size={{ sm: 3, xs: 12 }}>
            <Field name="end" label="Date" component={Inputs.Date} />
          </Grid>
          <Grid size={{ sm: 6, xs: 12 }}>
            <Field
              name="milestoneTitle"
              label="Title"
              component={Inputs.Text}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid container direction={"row"} justifyContent={"flex-end"}>
              <Grid>
                <Base.Button
                  text="Add Date"
                  onClick={() => {
                    handleAddMilestone(
                      {
                        start: values.start,
                        end: values.end,
                        title: values.milestoneTitle,
                      },
                      setFieldValue
                    );
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Grid
              size={{ xs: 12 }}
              sx={{
                "& .header": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.primary.contrastText,
                },
              }}
            >
              <Base.TableContainer
                columns={columns}
                rows={displayRows(
                  temporaryMilestones.length ? temporaryMilestones : []
                )}
                mobileRows={displayRows(
                  temporaryMilestones.length ? temporaryMilestones : []
                )}
                totalRows={temporaryMilestones.length}
                pageSize={5}
                highlightId={deleteMilestone?.id}
              />
            </Grid>
          </Grid>
          <Base.Dialog
            open={deleteMilestone?.id !== undefined}
            title={`Are you sure you would like to delete ${deleteMilestone?.value}?`}
            handleCancel={() =>
              setDeleteMilestone({ id: undefined, value: "" })
            }
            confirmText="Delete"
            confirmButtonType="delete"
            handleConfirm={() => {
              const filteredMilestones = temporaryMilestones
                .slice()
                .filter((m) => m.id !== deleteMilestone?.id);
              setTemporaryMilestones(filteredMilestones);
              setDeleteMilestone({ id: undefined, value: undefined });
            }}
            loading={false}
          />
        </Grid>
      );
    case AnswerTypes.DateTime:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.DateTime}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.MultipleChoice:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Dropdown}
          options={values.questions[index].choices}
          staticFilter
          label={question.questionText}
          required={question.required}
          multiple={question.multipleChoice}
          freeSolo={question.freeSolo}
        />
      );
    case AnswerTypes.Site:
      return (
        <Grid container spacing={2}>
          <Grid size={6}>
            <Field
              name={`questions.${index}.site`}
              component={Inputs.Dropdown}
              options={
                siteSearchResults?.results && siteFilter.query
                  ? siteSearchResults?.results.map((site) => {
                      return {
                        id: site.id,
                        value: `${site.name} ${
                          site.id > 0 ? `(${site?.siteType?.value})` : ""
                        }${
                          site.id > 0 && site.siteNumber
                            ? `: ${site?.siteNumber}`
                            : ""
                        }`,
                      };
                    })
                  : []
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                updateSiteSearch(e.currentTarget.value);
              }}
              noOptionsText={
                siteFilter.query === "" ? "Start typing to search" : undefined
              }
              label={question.questionText}
              required={question.required}
              onSelected={(
                e: React.SyntheticEvent,
                v: { id?: number; name?: string }
              ) => {
                setFieldValue(`questions.${index}.site`, undefined);
                if (!!v && !tempSites.some((s) => s.id === v.id)) {
                  setTempSites((old) => [
                    ...old,
                    { ...v, order: tempSites.length + 1 },
                  ]);
                }
              }}
              clearAfterSelect
            />
          </Grid>
          <Grid size={6} sx={{ height: "150px", overflow: "scroll" }}>
            <DndContext onDragEnd={reorder} collisionDetection={closestCenter}>
              <ul
                style={{
                  width: "100%",
                  margin: 0,
                  padding: 0,
                }}
              >
                <Typography align="center">Selected Sites</Typography>
                <Grid container direction={"column"} spacing={2}>
                  <SortableContext
                    items={tempSites.map((s) => s.order!)}
                    strategy={verticalListSortingStrategy}
                  >
                    {tempSites.map((s) => (
                      <SortableSitePreview
                        removeQuestion={() => {
                          const filtered = tempSites.filter(
                            (s2) => s2.id !== s.id
                          );
                          filtered.forEach((site, index) => {
                            site.order = index + 1;
                          });
                          setTempSites(filtered);
                        }}
                        site={s!}
                      />
                    ))}
                  </SortableContext>
                </Grid>
              </ul>
            </DndContext>
          </Grid>
        </Grid>
      );
    case AnswerTypes.Category:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Dropdown}
          options={categorySearchResults?.results || []}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateCategorySearch(e.currentTarget.value);
          }}
          label={question.questionText}
          required={question.required}
          noOptionsText={
            values.id === 0
              ? `Manage categories in settings after job type creation`
              : undefined
          }
        />
      );
    case AnswerTypes.Source:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Dropdown}
          options={jobSourceSearchResults?.results || []}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateJobSourceSearch(e.currentTarget.value);
          }}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.Method:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Dropdown}
          options={communicationMethodSearchResults?.results || []}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateCommMethodSearch(e.currentTarget.value);
          }}
          label={question.questionText}
          required={question.required}
        />
      );
    case AnswerTypes.WeeklyTask:
      return (
        <Grid container direction={"column"}>
          <Field
            name="highlight"
            component={Inputs.Checkbox}
            label="Weekly Task"
          />
          {!!values.highlight && (
            <Grid size={{ xs: 6 }}>
              <Field
                name={`questions.${index}.value`}
                component={Inputs.Date}
                disablePast
              />
            </Grid>
          )}
        </Grid>
      );
    case AnswerTypes.DateRange:
      return (
        <Field
          name={`questions.${index}.value`}
          label={question.questionText}
          component={Inputs.DateRange}
          required={question.required}
        />
      );
    case AnswerTypes.Date:
      return (
        <Field
          name={`questions.${index}.value`}
          label={question.questionText}
          component={Inputs.Date}
          required={question.required}
        />
      );
    case AnswerTypes.Time:
      return (
        <Field
          name={`questions.${index}.value`}
          label={question.questionText}
          component={Inputs.Time}
          required={question.required}
        />
      );
    case AnswerTypes.Job:
      return (
        <Field
          name={`questions.${index}.value`}
          label={question.questionText}
          component={Inputs.Dropdown}
          options={jobByTypeResults.concat(values.questions[index].choices)}
          required={question.required}
          multiple={question.multipleChoice}
          freeSolo={question.freeSolo}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            updateJobSearch(e.currentTarget.value);
          }}
        />
      );
    case AnswerTypes.Confidential:
      return (
        <Field
          name={`questions.${index}.value`}
          component={Inputs.Checkbox}
          label={"Confidential"}
          required={question.required}
          confidential
          fontVariant="h5"
        />
      );
    case AnswerTypes.Section:
      return (
        <>
          {!!question.questionText.length && (
            <Typography variant="h4">{question.questionText}</Typography>
          )}
          <Divider />
        </>
      );
    default:
      return <>Other</>;
  }
};

export default QuestionPreview;
